/**
 * „Mein Konto" — Design 11 (Turn 11, Optionen 11a–c), eingebettet in die MAESTRO-Shell.
 * Vier Tabs: Profil · E-Mail & Anmeldung · Benachrichtigungen · Sitzungen.
 * Konto (Person) ≠ Einstellungen (Betrieb): hier NUR Persönliches, alles zum Betrieb bleibt
 * unter „Einstellungen" in der Hauptnav.
 *
 * Persistenz OHNE DB-Migration: Vollständiger Name -> Stack `displayName`; Anzeigename, Telefon,
 * Sprache und die Benachrichtigungs-Matrix -> Stack `clientMetadata` (JSON am User). Darstellung
 * -> gemeinsames Theme (localStorage, wirkt sofort). Sicherheits-Flows (Passwort, 2FA, E-Mail
 * hinzufügen/verifizieren) delegieren an Stacks geprüfte Account-Flows.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { useApi } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me } from "../ui/types";
import { PageHeader, Icon, ErrorNote } from "../ui/components";
import { ROLE_LABEL } from "../ui/format";
import { useTheme, THEME_OPTS } from "../ui/theme";

type TabKey = "profil" | "email" | "benachrichtigungen" | "sitzungen";
const TABS: { key: TabKey; label: string }[] = [
  { key: "profil", label: "Profil" },
  { key: "email", label: "E-Mail & Anmeldung" },
  { key: "benachrichtigungen", label: "Benachrichtigungen" },
  { key: "sitzungen", label: "Sitzungen" },
];

// Benachrichtigungs-Ereignisse (Design 11b).
const EVENTS: { key: string; label: string; hint: string }[] = [
  { key: "neue_anfrage", label: "Neue Anfrage", hint: "Über Website-Formular, KI-Chat, E-Mail oder Telefon" },
  { key: "angebot_angenommen", label: "Angebot angenommen", hint: "Kunde hat auf der Angebotsseite zugesagt" },
  { key: "angebot_abgelehnt", label: "Angebot abgelehnt", hint: "Kunde hat abgesagt oder nicht reagiert" },
  { key: "zahlung_eingegangen", label: "Zahlung eingegangen", hint: "Anzahlung oder Rechnung wurde bezahlt" },
  { key: "zahlung_ueberfaellig", label: "Zahlung überfällig", hint: "Anzahlung oder Rechnung nicht fristgerecht beglichen" },
  { key: "chat_nachricht", label: "Neue Nachricht im KI-Chat", hint: "Interessent schreibt über den öffentlichen Chat" },
];

const LANGS: { value: string; label: string }[] = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
];

type EventPref = { email: boolean; push: boolean };
type NotifPrefs = {
  events: Record<string, EventPref>;
  dailySummary: boolean;
  weeklyReport: boolean;
  productNews: boolean;
};
type ProfileForm = { displayName: string; nickname: string; phone: string; language: string };

type AccountMeta = {
  nickname?: string;
  phone?: string;
  language?: string;
  notifications?: Partial<NotifPrefs>;
};

function defaultNotif(): NotifPrefs {
  const events: Record<string, EventPref> = {};
  for (const e of EVENTS) events[e.key] = { email: true, push: false };
  return { events, dailySummary: true, weeklyReport: false, productNews: true };
}

function readNotif(meta: AccountMeta): NotifPrefs {
  const base = defaultNotif();
  const n = meta.notifications;
  if (!n) return base;
  return {
    events: Object.fromEntries(
      EVENTS.map((e) => [e.key, { ...base.events[e.key]!, ...(n.events?.[e.key] ?? {}) }]),
    ),
    dailySummary: n.dailySummary ?? base.dailySummary,
    weeklyReport: n.weeklyReport ?? base.weeklyReport,
    productNews: n.productNews ?? base.productNews,
  };
}

/** Kleiner Ein/Aus-Schalter für die Matrix (E-Mail/Push je Ereignis). */
function MiniToggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-primary" : "bg-surface-variant dark:bg-white/15"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-[3px]"
        }`}
      />
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
      {children}
    </span>
  );
}

/** Passwort-ändern-Dialog im MAESTRO-CI (nativ über Stack `updatePassword`, kein Sprung zur alten Seite). */
function PasswordDialog({
  user,
  onClose,
}: {
  user: { hasPassword: boolean; updatePassword: (d: { oldPassword: string; newPassword: string }) => Promise<unknown> };
  onClose: () => void;
}) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    setErr(null);
    if (newPw.length < 8) return setErr("Das neue Passwort muss mindestens 8 Zeichen haben.");
    if (newPw !== confirm) return setErr("Die Passwörter stimmen nicht überein.");
    setBusy(true);
    try {
      const res = await user.updatePassword({ oldPassword: oldPw, newPassword: newPw });
      if (res) {
        setErr("Passwort konnte nicht geändert werden. Ist das aktuelle Passwort korrekt und das neue sicher genug?");
      } else {
        setDone(true);
        setTimeout(onClose, 1200);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 dark:bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl dark:border-white/10 dark:bg-night-card">
        <h2 className="mb-1 font-headline-sm text-[17px] font-bold text-on-background dark:text-inverse-on-surface">Passwort ändern</h2>
        <p className="mb-5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          {user.hasPassword ? "Gib dein aktuelles und ein neues Passwort ein." : "Lege ein Passwort für dein Konto fest."}
        </p>
        {done ? (
          <div className="flex items-center gap-2 rounded-lg bg-green-500/10 px-4 py-3 font-body-sm text-body-sm text-green-700 dark:text-green-300">
            <Icon name="check_circle" className="text-[18px]" /> Passwort geändert.
          </div>
        ) : (
          <div className="space-y-4">
            {user.hasPassword && (
              <label className="block">
                <FieldLabel>Aktuelles Passwort</FieldLabel>
                <input className="input" type="password" autoComplete="current-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
              </label>
            )}
            <label className="block">
              <FieldLabel>Neues Passwort</FieldLabel>
              <input className="input" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            </label>
            <label className="block">
              <FieldLabel>Neues Passwort bestätigen</FieldLabel>
              <input className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </label>
            {err && <p className="font-body-sm text-body-sm text-red-600 dark:text-red-400">{err}</p>}
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Abbrechen
              </button>
              <button type="button" className="btn-primary" onClick={() => void submit()} disabled={busy}>
                {busy ? "Speichert …" : "Passwort ändern"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MeinKontoPage() {
  const user = useUser({ or: "redirect" });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: me } = useApi<Me>("/api/me");
  const [theme, setTheme] = useTheme();
  // E-Mail-Kanäle direkt aus Stack (nativer Verify-Resend im neuen CI, kein Sprung zur alten Seite).
  const channels = user.useContactChannels().filter((c) => c.type === "email");
  const [resentTo, setResentTo] = useState<string | null>(null);
  const [pwOpen, setPwOpen] = useState(false);

  const membership = currentMembership(me);
  const roleLabel = membership?.role ? (ROLE_LABEL[membership.role] ?? membership.role) : undefined;

  const tabParam = params.get("tab") as TabKey | null;
  const tab: TabKey = TABS.some((t) => t.key === tabParam) ? tabParam! : "profil";
  const setTab = (t: TabKey) => setParams(t === "profil" ? {} : { tab: t }, { replace: true });

  const meta = (user?.clientMetadata ?? {}) as AccountMeta;
  const serverProfile = useMemo<ProfileForm>(
    () => ({
      displayName: user?.displayName ?? "",
      nickname: meta.nickname ?? "",
      phone: meta.phone ?? "",
      language: meta.language ?? "de",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, user?.displayName, meta.nickname, meta.phone, meta.language],
  );
  const serverNotif = useMemo(() => readNotif(meta), [user?.id, meta.notifications]);

  const [profile, setProfile] = useState<ProfileForm>(serverProfile);
  const [notif, setNotif] = useState<NotifPrefs>(serverNotif);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<null | "profil" | "benachrichtigungen">(null);

  // Serverwerte in die Formulare spiegeln, sobald der User geladen/gewechselt ist.
  useEffect(() => setProfile(serverProfile), [serverProfile]);
  useEffect(() => setNotif(serverNotif), [serverNotif]);

  const profileDirty = JSON.stringify(profile) !== JSON.stringify(serverProfile);
  const notifDirty = JSON.stringify(notif) !== JSON.stringify(serverNotif);

  async function persist(next: { profile?: ProfileForm; notif?: NotifPrefs }, which: "profil" | "benachrichtigungen") {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const p = next.profile ?? profile;
      const n = next.notif ?? notif;
      // clientMetadata mergen (andere Keys nicht überschreiben).
      const nextMeta: AccountMeta = {
        ...meta,
        nickname: p.nickname || undefined,
        phone: p.phone || undefined,
        language: p.language,
        notifications: n,
      };
      await user.update({ displayName: p.displayName.trim(), clientMetadata: nextMeta });
      setSaved(which);
      setTimeout(() => setSaved((s) => (s === which ? null : s)), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const initials = (() => {
    const src = (user?.displayName ?? "").trim();
    if (src) {
      const parts = src.split(/\s+/);
      return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
    }
    return (user?.primaryEmail ?? "?").slice(0, 1).toUpperCase();
  })();

  return (
    <>
      <PageHeader
        title="Mein Konto"
        subtitle={
          <>
            Deine persönlichen Einstellungen. Alles zum Betrieb findest du unter{" "}
            <button type="button" className="text-primary hover:underline" onClick={() => navigate("/einstellungen")}>
              Einstellungen
            </button>{" "}
            in der Navigation.
          </>
        }
      />

      {/* Tab-Leiste */}
      <div className="tab-scroll mb-6 flex gap-1 border-b border-outline-variant/30 dark:border-white/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 font-body-sm text-body-sm font-medium transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-on-surface-variant hover:text-on-background dark:text-surface-variant dark:hover:text-inverse-on-surface"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6">
          <ErrorNote message={error} />
        </div>
      )}

      {/* ---------- Profil ---------- */}
      {tab === "profil" && (
        <div className="max-w-2xl space-y-6">
          {/* Profilbild */}
          <div className="card flex items-center gap-4 p-6">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-primary/15 font-serif text-2xl font-bold text-primary">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Profilbild</p>
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">JPG oder PNG, max. 2 MB</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" className="btn-secondary" onClick={() => navigate("/handler/account-settings")}>
                Foto ändern
              </button>
            </div>
          </div>

          {/* Formular */}
          <div className="card space-y-5 p-6">
            <label className="block">
              <FieldLabel>Vollständiger Name</FieldLabel>
              <input
                className="input"
                value={profile.displayName}
                onChange={(e) => setProfile((p) => ({ ...p, displayName: e.target.value }))}
                placeholder="Vor- und Nachname"
              />
            </label>
            <label className="block">
              <FieldLabel>Anzeigename</FieldLabel>
              <input
                className="input"
                value={profile.nickname}
                onChange={(e) => setProfile((p) => ({ ...p, nickname: e.target.value }))}
                placeholder="z. B. Jonas"
              />
              <span className="mt-1.5 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Erscheint im Team und in Aktivitäten
              </span>
            </label>
            <label className="block">
              <FieldLabel>Telefon</FieldLabel>
              <input
                className="input"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+49 89 1234 567"
              />
              <span className="mt-1.5 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Für Rückfragen zu Veranstaltungen
              </span>
            </label>

            {/* Rolle (nur Anzeige) */}
            <div>
              <FieldLabel>Rolle</FieldLabel>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-surface-container-high px-3 py-1 font-body-sm text-body-sm font-semibold text-on-background dark:bg-white/10 dark:text-inverse-on-surface">
                  {roleLabel ?? "—"}
                </span>
                <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Rollen verwaltest du unter{" "}
                  <button type="button" className="text-primary hover:underline" onClick={() => navigate("/einstellungen")}>
                    Team &amp; Rollen
                  </button>
                </span>
              </div>
            </div>

            <label className="block">
              <FieldLabel>Sprache</FieldLabel>
              <select
                className="input"
                value={profile.language}
                onChange={(e) => setProfile((p) => ({ ...p, language: e.target.value }))}
              >
                {LANGS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>

            {/* Darstellung — wirkt sofort (wie im Konto-Menü) */}
            <div>
              <FieldLabel>Darstellung</FieldLabel>
              <div className="inline-flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-white/5">
                {THEME_OPTS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTheme(opt.value)}
                    aria-pressed={theme === opt.value}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 font-body-sm text-body-sm font-medium transition-colors ${
                      theme === opt.value
                        ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                        : "text-on-surface-variant hover:text-on-background dark:text-surface-variant dark:hover:text-inverse-on-surface"
                    }`}
                  >
                    <Icon name={opt.icon} className="text-[16px]" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Speicherleiste */}
          <div className="flex items-center justify-end gap-3">
            {saved === "profil" && (
              <span className="flex items-center gap-1 font-body-sm text-body-sm text-green-600 dark:text-green-400">
                <Icon name="check" className="text-[18px]" /> Gespeichert
              </span>
            )}
            <button type="button" className="btn-secondary" disabled={!profileDirty || busy} onClick={() => setProfile(serverProfile)}>
              Zurücksetzen
            </button>
            <button type="button" className="btn-primary" disabled={!profileDirty || busy} onClick={() => void persist({ profile }, "profil")}>
              {busy ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- E-Mail & Anmeldung ---------- */}
      {tab === "email" && (
        <div className="max-w-2xl space-y-6">
          {/* E-Mail-Adressen */}
          <div className="card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">E-Mail-Adressen</p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Für Anmeldung und Benachrichtigungen</p>
              </div>
              <button type="button" className="btn-secondary" onClick={() => navigate("/handler/account-settings")}>
                <Icon name="add" className="text-[18px]" /> E-Mail hinzufügen
              </button>
            </div>
            <div className="overflow-hidden rounded-xl border border-outline-variant/40 dark:border-outline/30">
              {channels.length === 0 && (
                <div className="px-4 py-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {user.primaryEmail ?? "—"}
                </div>
              )}
              {channels.map((ch, i) => (
                <div key={ch.id} className={i > 0 ? "border-t border-outline-variant/20 dark:border-white/[0.06]" : ""}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Icon name="mail" className="text-[20px] text-on-surface-variant dark:text-surface-variant" />
                    <span className="min-w-0 flex-1 truncate font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">
                      {ch.value}
                    </span>
                    {ch.isPrimary && (
                      <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/10 dark:text-surface-variant">
                        Primär
                      </span>
                    )}
                    {ch.isVerified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-500/12 px-2 py-0.5 text-[10.5px] font-semibold text-green-700 dark:text-green-300">
                        <Icon name="check" className="text-[13px]" /> Bestätigt
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 dark:text-amber-300">
                        <Icon name="warning" className="text-[13px]" /> Nicht bestätigt
                      </span>
                    )}
                  </div>
                  {!ch.isVerified && (
                    <div className="flex items-center justify-between gap-3 border-t border-amber-500/20 bg-amber-500/[0.06] px-4 py-2.5">
                      <span className="font-body-sm text-body-sm text-amber-700 dark:text-amber-300">
                        {resentTo === ch.value
                          ? "Bestätigungslink gesendet — bitte Postfach prüfen."
                          : "Diese Adresse ist noch nicht bestätigt."}
                      </span>
                      {resentTo !== ch.value && (
                        <button
                          type="button"
                          className="shrink-0 font-body-sm text-body-sm font-semibold text-amber-800 underline dark:text-amber-200"
                          onClick={async () => {
                            setError(null);
                            try {
                              await ch.sendVerificationEmail();
                              setResentTo(ch.value);
                            } catch (e) {
                              setError(e instanceof Error ? e.message : String(e));
                            }
                          }}
                        >
                          Bestätigungslink erneut senden
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Passwort */}
          <div className="card flex items-center justify-between gap-4 p-6">
            <div>
              <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Passwort</p>
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Regelmäßig ändern erhöht die Sicherheit deines Kontos.
              </p>
            </div>
            <button type="button" className="btn-secondary shrink-0" onClick={() => setPwOpen(true)}>
              Passwort ändern
            </button>
          </div>

          {/* 2FA */}
          <div className="card flex items-center justify-between gap-4 p-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                  Zwei-Faktor-Authentifizierung
                </p>
                <span className="rounded-full bg-primary/12 px-2 py-0.5 text-[10.5px] font-semibold text-primary">Empfohlen</span>
              </div>
              {/* M-25: Der Status wird NICHT mehr hart als "deaktiviert" behauptet (die Seite
                  liest den echten MFA-Faktor nicht). Verwaltung liegt in den Kontoeinstellungen
                  des Identity-Providers, wo der tatsaechliche Zustand sichtbar ist. */}
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Zusätzlicher Schutz bei der Anmeldung. Status und Einrichtung in den Kontoeinstellungen.
              </p>
            </div>
            <button type="button" className="btn-primary shrink-0" onClick={() => navigate("/handler/account-settings")}>
              Verwalten
            </button>
          </div>
        </div>
      )}

      {/* ---------- Benachrichtigungen ---------- */}
      {tab === "benachrichtigungen" && (
        <div className="max-w-2xl space-y-6">
          {/* Ereignis-Matrix */}
          <div className="card p-6">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Ereignisse im Betrieb</p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Wobei möchtest du benachrichtigt werden — und wie?
                </p>
              </div>
              <div className="flex shrink-0 gap-3 pb-0.5 text-center font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                <span className="w-9">E-Mail</span>
                <span className="w-9">Push</span>
              </div>
            </div>
            <div className="divide-y divide-outline-variant/30 dark:divide-white/[0.06]">
              {EVENTS.map((ev) => (
                <div key={ev.key} className="flex items-center gap-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">{ev.label}</p>
                    <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{ev.hint}</p>
                  </div>
                  <div className="flex shrink-0 gap-3">
                    <span className="grid w-9 place-items-center">
                      <MiniToggle
                        label={`${ev.label} E-Mail`}
                        checked={notif.events[ev.key]?.email ?? false}
                        onChange={(v) =>
                          setNotif((n) => ({ ...n, events: { ...n.events, [ev.key]: { ...n.events[ev.key]!, email: v } } }))
                        }
                      />
                    </span>
                    <span className="grid w-9 place-items-center">
                      <MiniToggle
                        label={`${ev.label} Push`}
                        checked={notif.events[ev.key]?.push ?? false}
                        onChange={(v) =>
                          setNotif((n) => ({ ...n, events: { ...n.events, [ev.key]: { ...n.events[ev.key]!, push: v } } }))
                        }
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Zusammenfassungen */}
          <div className="card p-6">
            <p className="mb-3 font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Zusammenfassungen</p>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">Tägliche Zusammenfassung</p>
                  <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Jeden Morgen um 8:00 Uhr — anstehende Events, offene Aufgaben
                  </p>
                </div>
                <MiniToggle label="Tägliche Zusammenfassung" checked={notif.dailySummary} onChange={(v) => setNotif((n) => ({ ...n, dailySummary: v }))} />
              </div>
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">Wöchentlicher Report</p>
                  <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Montags — Kennzahlen und Umsatz der Vorwoche
                  </p>
                </div>
                <MiniToggle label="Wöchentlicher Report" checked={notif.weeklyReport} onChange={(v) => setNotif((n) => ({ ...n, weeklyReport: v }))} />
              </div>
            </div>
          </div>

          {/* E-Mails von MAESTRO */}
          <div className="card p-6">
            <p className="mb-3 font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">E-Mails von MAESTRO</p>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">Transaktionale E-Mails</p>
                  <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Angebote, Bestätigungen, Rechnungen — für den Betrieb notwendig
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-surface-container-high px-2.5 py-1 font-body-sm text-[11px] font-semibold text-on-surface-variant dark:bg-white/10 dark:text-surface-variant">
                  Immer aktiv
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">Produkt-News &amp; Tipps</p>
                  <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Neue Funktionen und Tipps für dein Catering-Geschäft
                  </p>
                </div>
                <MiniToggle label="Produkt-News und Tipps" checked={notif.productNews} onChange={(v) => setNotif((n) => ({ ...n, productNews: v }))} />
              </div>
            </div>
          </div>

          {/* Speicherleiste */}
          <div className="flex items-center justify-end gap-3">
            {saved === "benachrichtigungen" && (
              <span className="flex items-center gap-1 font-body-sm text-body-sm text-green-600 dark:text-green-400">
                <Icon name="check" className="text-[18px]" /> Gespeichert
              </span>
            )}
            <button type="button" className="btn-secondary" disabled={!notifDirty || busy} onClick={() => setNotif(serverNotif)}>
              Zurücksetzen
            </button>
            <button type="button" className="btn-primary" disabled={!notifDirty || busy} onClick={() => void persist({ notif }, "benachrichtigungen")}>
              {busy ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- Sitzungen (Design: als Nächstes) ---------- */}
      {tab === "sitzungen" && (
        <div className="max-w-2xl">
          <div className="card flex flex-col items-center gap-3 p-10 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-surface-container-high text-on-surface-variant dark:bg-white/10 dark:text-surface-variant">
              <Icon name="devices" className="text-2xl" />
            </div>
            <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Aktive Geräte &amp; Sitzungen</p>
            <p className="max-w-sm font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Eine Übersicht deiner angemeldeten Geräte folgt in Kürze. Bis dahin kannst du dich in den Konto-Einstellungen
              überall abmelden.
            </p>
            <button type="button" className="btn-secondary" onClick={() => navigate("/handler/account-settings")}>
              Zu den Konto-Einstellungen
            </button>
          </div>
        </div>
      )}

      {pwOpen && <PasswordDialog user={user} onClose={() => setPwOpen(false)} />}
    </>
  );
}
